import { transformToViewState, applyViewStateToTransform, cloneTransform, syncProjection } from "../utils/transform.js";
import { normalizeStyle } from "../utils/style-utils.js";
import { deepEqual } from "../utils/deep-equal.js";
const DEFAULT_STYLE = {
  version: 8,
  sources: {},
  layers: []
};
const pointerEvents = {
  mousedown: 'onMouseDown',
  mouseup: 'onMouseUp',
  mouseover: 'onMouseOver',
  mousemove: 'onMouseMove',
  click: 'onClick',
  dblclick: 'onDblClick',
  mouseenter: 'onMouseEnter',
  mouseleave: 'onMouseLeave',
  mouseout: 'onMouseOut',
  contextmenu: 'onContextMenu',
  touchstart: 'onTouchStart',
  touchend: 'onTouchEnd',
  touchmove: 'onTouchMove',
  touchcancel: 'onTouchCancel'
};
const cameraEvents = {
  movestart: 'onMoveStart',
  move: 'onMove',
  moveend: 'onMoveEnd',
  dragstart: 'onDragStart',
  drag: 'onDrag',
  dragend: 'onDragEnd',
  zoomstart: 'onZoomStart',
  zoom: 'onZoom',
  zoomend: 'onZoomEnd',
  rotatestart: 'onRotateStart',
  rotate: 'onRotate',
  rotateend: 'onRotateEnd',
  pitchstart: 'onPitchStart',
  pitch: 'onPitch',
  pitchend: 'onPitchEnd'
};
const otherEvents = {
  wheel: 'onWheel',
  boxzoomstart: 'onBoxZoomStart',
  boxzoomend: 'onBoxZoomEnd',
  boxzoomcancel: 'onBoxZoomCancel',
  resize: 'onResize',
  load: 'onLoad',
  render: 'onRender',
  idle: 'onIdle',
  remove: 'onRemove',
  data: 'onData',
  styledata: 'onStyleData',
  sourcedata: 'onSourceData',
  error: 'onError'
};
const settingNames = ['minZoom', 'maxZoom', 'minPitch', 'maxPitch', 'maxBounds', 'projection', 'renderWorldCopies'];
const handlerNames = ['scrollZoom', 'boxZoom', 'dragRotate', 'dragPan', 'keyboard', 'doubleClickZoom', 'touchZoomRotate', 'touchPitch'];
export default class Mapbox {
  constructor(MapClass, props, container) {
    this._MapClass = void 0;
    this._map = null;
    this.props = void 0;
    this._renderTransform = void 0;
    this._internalUpdate = false;
    this._inRender = false;
    this._hoveredFeatures = null;
    this._deferredEvents = {
      move: false,
      zoom: false,
      pitch: false,
      rotate: false
    };

    this._onEvent = e => {
      const cb = this.props[otherEvents[e.type]];

      if (cb) {
        cb(e);
      } else if (e.type === 'error') {
        console.error(e.error);
      }
    };

    this._onPointerEvent = e => {
      if (e.type === 'mousemove' || e.type === 'mouseout') {
        this._updateHover(e);
      }

      const cb = this.props[pointerEvents[e.type]];

      if (cb) {
        if (this.props.interactiveLayerIds && e.type !== 'mouseover' && e.type !== 'mouseout') {
          e.features = this._hoveredFeatures || this._queryRenderedFeatures(e.point);
        }

        cb(e);
        delete e.features;
      }
    };

    this._onCameraEvent = e => {
      if (!this._internalUpdate) {
        const cb = this.props[cameraEvents[e.type]];

        if (cb) {
          cb(e);
        }
      }

      if (e.type in this._deferredEvents) {
        this._deferredEvents[e.type] = false;
      }
    };

    this._onAfterRepaint = void 0;
    this._MapClass = MapClass;
    this.props = props;

    this._initialize(container);
  }

  get map() {
    return this._map;
  }

  get transform() {
    return this._renderTransform;
  }

  setProps(props) {
    const oldProps = this.props;
    this.props = props;

    const settingsChanged = this._updateSettings(props, oldProps);

    if (settingsChanged) {
      this._createShadowTransform(this._map);
    }

    const sizeChanged = this._updateSize(props);

    const viewStateChanged = this._updateViewState(props, true);

    this._updateStyle(props, oldProps);

    this._updateStyleComponents(props, oldProps);

    this._updateHandlers(props, oldProps);

    if (settingsChanged || sizeChanged || viewStateChanged && !this._map.isMoving()) {
      this.redraw();
    }
  }

  static reuse(props, container) {
    const that = Mapbox.savedMaps.pop();

    if (!that) {
      return null;
    }

    const map = that.map;
    const oldContainer = map.getContainer();
    container.className = oldContainer.className;

    while (oldContainer.childNodes.length > 0) {
      container.appendChild(oldContainer.childNodes[0]);
    }

    map._container = container;
    const resizeObserver = map._resizeObserver;

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver.observe(container);
    }

    that.setProps({ ...props,
      styleDiffing: false
    });
    map.resize();
    const {
      initialViewState
    } = props;

    if (initialViewState) {
      if (initialViewState.bounds) {
        map.fitBounds(initialViewState.bounds, { ...initialViewState.fitBoundsOptions,
          duration: 0
        });
      } else {
        that._updateViewState(initialViewState, false);
      }
    }

    if (map.isStyleLoaded()) {
      map.fire('load');
    } else {
      map.once('styledata', () => map.fire('load'));
    }

    map._update();

    return that;
  }

  _initialize(container) {
    const {
      props
    } = this;
    const {
      mapStyle = DEFAULT_STYLE
    } = props;
    const mapOptions = { ...props,
      ...props.initialViewState,
      accessToken: props.mapboxAccessToken || getAccessTokenFromEnv() || null,
      container,
      style: normalizeStyle(mapStyle)
    };
    const viewState = mapOptions.initialViewState || mapOptions.viewState || mapOptions;
    Object.assign(mapOptions, {
      center: [viewState.longitude || 0, viewState.latitude || 0],
      zoom: viewState.zoom || 0,
      pitch: viewState.pitch || 0,
      bearing: viewState.bearing || 0
    });

    if (props.gl) {
      const getContext = HTMLCanvasElement.prototype.getContext;

      HTMLCanvasElement.prototype.getContext = () => {
        HTMLCanvasElement.prototype.getContext = getContext;
        return props.gl;
      };
    }

    const map = new this._MapClass(mapOptions);

    if (viewState.padding) {
      map.setPadding(viewState.padding);
    }

    if (props.cursor) {
      map.getCanvas().style.cursor = props.cursor;
    }

    this._createShadowTransform(map);

    const renderMap = map._render;

    map._render = arg => {
      this._inRender = true;
      renderMap.call(map, arg);
      this._inRender = false;
    };

    const runRenderTaskQueue = map._renderTaskQueue.run;

    map._renderTaskQueue.run = arg => {
      runRenderTaskQueue.call(map._renderTaskQueue, arg);

      this._onBeforeRepaint();
    };

    map.on('render', () => this._onAfterRepaint());
    const fireEvent = map.fire;
    map.fire = this._fireEvent.bind(this, fireEvent);
    map.on('resize', () => {
      this._renderTransform.resize(map.transform.width, map.transform.height);
    });
    map.on('styledata', () => {
      this._updateStyleComponents(this.props, {});

      syncProjection(map.transform, this._renderTransform);
    });
    map.on('sourcedata', () => this._updateStyleComponents(this.props, {}));

    for (const eventName in pointerEvents) {
      map.on(eventName, this._onPointerEvent);
    }

    for (const eventName in cameraEvents) {
      map.on(eventName, this._onCameraEvent);
    }

    for (const eventName in otherEvents) {
      map.on(eventName, this._onEvent);
    }

    this._map = map;
  }

  recycle() {
    const container = this.map.getContainer();
    const children = container.querySelector('[mapboxgl-children]');
    children === null || children === void 0 ? void 0 : children.remove();
    Mapbox.savedMaps.push(this);
  }

  destroy() {
    this._map.remove();
  }

  redraw() {
    const map = this._map;

    if (!this._inRender && map.style) {
      if (map._frame) {
        map._frame.cancel();

        map._frame = null;
      }

      map._render();
    }
  }

  _createShadowTransform(map) {
    const renderTransform = cloneTransform(map.transform);
    map.painter.transform = renderTransform;
    this._renderTransform = renderTransform;
  }

  _updateSize(nextProps) {
    const {
      viewState
    } = nextProps;

    if (viewState) {
      const map = this._map;

      if (viewState.width !== map.transform.width || viewState.height !== map.transform.height) {
        map.resize();
        return true;
      }
    }

    return false;
  }

  _updateViewState(nextProps, triggerEvents) {
    if (this._internalUpdate) {
      return false;
    }

    const map = this._map;
    const tr = this._renderTransform;
    const {
      zoom,
      pitch,
      bearing
    } = tr;
    const isMoving = map.isMoving();

    if (isMoving) {
      tr.cameraElevationReference = 'sea';
    }

    const changed = applyViewStateToTransform(tr, { ...transformToViewState(map.transform),
      ...nextProps
    });

    if (isMoving) {
      tr.cameraElevationReference = 'ground';
    }

    if (changed && triggerEvents) {
      const deferredEvents = this._deferredEvents;
      deferredEvents.move = true;
      deferredEvents.zoom || (deferredEvents.zoom = zoom !== tr.zoom);
      deferredEvents.rotate || (deferredEvents.rotate = bearing !== tr.bearing);
      deferredEvents.pitch || (deferredEvents.pitch = pitch !== tr.pitch);
    }

    if (!isMoving) {
      applyViewStateToTransform(map.transform, nextProps);
    }

    return changed;
  }

  _updateSettings(nextProps, currProps) {
    const map = this._map;
    let changed = false;

    for (const propName of settingNames) {
      if (propName in nextProps && !deepEqual(nextProps[propName], currProps[propName])) {
        changed = true;
        const setter = map[`set${propName[0].toUpperCase()}${propName.slice(1)}`];
        setter === null || setter === void 0 ? void 0 : setter.call(map, nextProps[propName]);
      }
    }

    return changed;
  }

  _updateStyle(nextProps, currProps) {
    if (nextProps.cursor !== currProps.cursor) {
      this._map.getCanvas().style.cursor = nextProps.cursor || '';
    }

    if (nextProps.mapStyle !== currProps.mapStyle) {
      const {
        mapStyle = DEFAULT_STYLE,
        styleDiffing = true
      } = nextProps;
      const options = {
        diff: styleDiffing
      };

      if ('localIdeographFontFamily' in nextProps) {
        options.localIdeographFontFamily = nextProps.localIdeographFontFamily;
      }

      this._map.setStyle(normalizeStyle(mapStyle), options);

      return true;
    }

    return false;
  }

  _updateStyleComponents(nextProps, currProps) {
    const map = this._map;
    let changed = false;

    if (map.isStyleLoaded()) {
      if ('light' in nextProps && map.setLight && !deepEqual(nextProps.light, currProps.light)) {
        changed = true;
        map.setLight(nextProps.light);
      }

      if ('fog' in nextProps && map.setFog && !deepEqual(nextProps.fog, currProps.fog)) {
        changed = true;
        map.setFog(nextProps.fog);
      }

      if ('terrain' in nextProps && map.setTerrain && !deepEqual(nextProps.terrain, currProps.terrain)) {
        if (!nextProps.terrain || map.getSource(nextProps.terrain.source)) {
          changed = true;
          map.setTerrain(nextProps.terrain);
        }
      }
    }

    return changed;
  }

  _updateHandlers(nextProps, currProps) {
    const map = this._map;
    let changed = false;

    for (const propName of handlerNames) {
      var _nextProps$propName, _currProps$propName;

      const newValue = (_nextProps$propName = nextProps[propName]) !== null && _nextProps$propName !== void 0 ? _nextProps$propName : true;
      const oldValue = (_currProps$propName = currProps[propName]) !== null && _currProps$propName !== void 0 ? _currProps$propName : true;

      if (!deepEqual(newValue, oldValue)) {
        changed = true;

        if (newValue) {
          map[propName].enable(newValue);
        } else {
          map[propName].disable();
        }
      }
    }

    return changed;
  }

  _queryRenderedFeatures(point) {
    const map = this._map;
    const tr = map.transform;
    const {
      interactiveLayerIds = []
    } = this.props;

    try {
      map.transform = this._renderTransform;
      return map.queryRenderedFeatures(point, {
        layers: interactiveLayerIds.filter(map.getLayer.bind(map))
      });
    } catch {
      return [];
    } finally {
      map.transform = tr;
    }
  }

  _updateHover(e) {
    const {
      props
    } = this;
    const shouldTrackHoveredFeatures = props.interactiveLayerIds && (props.onMouseMove || props.onMouseEnter || props.onMouseLeave);

    if (shouldTrackHoveredFeatures) {
      var _this$_hoveredFeature;

      const eventType = e.type;
      const wasHovering = ((_this$_hoveredFeature = this._hoveredFeatures) === null || _this$_hoveredFeature === void 0 ? void 0 : _this$_hoveredFeature.length) > 0;

      const features = this._queryRenderedFeatures(e.point);

      const isHovering = features.length > 0;

      if (!isHovering && wasHovering) {
        e.type = 'mouseleave';

        this._onPointerEvent(e);
      }

      this._hoveredFeatures = features;

      if (isHovering && !wasHovering) {
        e.type = 'mouseenter';

        this._onPointerEvent(e);
      }

      e.type = eventType;
    } else {
      this._hoveredFeatures = null;
    }
  }

  _fireEvent(baseFire, event, properties) {
    const map = this._map;
    const tr = map.transform;
    const eventType = typeof event === 'string' ? event : event.type;

    if (eventType === 'move') {
      this._updateViewState(this.props, false);
    }

    if (eventType in cameraEvents) {
      if (typeof event === 'object') {
        event.viewState = transformToViewState(tr);
      }

      if (this._map.isMoving()) {
        map.transform = this._renderTransform;
        baseFire.call(map, event, properties);
        map.transform = tr;
        return map;
      }
    }

    baseFire.call(map, event, properties);
    return map;
  }

  _onBeforeRepaint() {
    const map = this._map;
    this._internalUpdate = true;

    for (const eventType in this._deferredEvents) {
      if (this._deferredEvents[eventType]) {
        map.fire(eventType);
      }
    }

    this._internalUpdate = false;
    const tr = this._map.transform;
    map.transform = this._renderTransform;

    this._onAfterRepaint = () => {
      syncProjection(this._renderTransform, tr);
      map.transform = tr;
    };
  }

}
Mapbox.savedMaps = [];

function getAccessTokenFromEnv() {
  let accessToken = null;

  if (typeof location !== 'undefined') {
    const match = /access_token=([^&\/]*)/.exec(location.search);
    accessToken = match && match[1];
  }

  try {
    accessToken = accessToken || process.env.MapboxAccessToken;
  } catch {}

  try {
    accessToken = accessToken || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  } catch {}

  return accessToken;
}
//# sourceMappingURL=mapbox.js.map